import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate, pct, learnerLabel } from "@/lib/format";
import { PageHeader, Panel, StatusBadge, DataTable, ProgressBar, EmptyState, SkeletonPage } from "@/components/common";
import { HelpLink } from "@/pages/help/Manual";
import { CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

// Outstanding work is the default view — finished work lives in the Transcript.
const FILTERS = [["outstanding", "To do"], ["overdue", "Overdue"], ["due_soon", "Due soon"], ["in_progress", "In progress"], ["required", "Required"], ["optional", "Optional"], ["locked", "Locked"], ["completed", "Completed"], ["all", "Everything"]];
const DONE = ["completed", "satisfied_by_equivalency"];

export default function MyLearning() {
  const [rows, setRows] = useState(null);
  const [params, setParams] = useSearchParams();
  const raw = params.get("filter") || "outstanding";
  const filter = raw === "assigned" ? "required" : raw === "elective" ? "optional" : raw;
  useEffect(() => { api.get("/learning/enrollments/me").then((r) => setRows(r.data)); }, []);

  const filtered = useMemo(() => (rows || []).filter((e) => {
    const done = DONE.includes(e.status);
    if (filter === "all") return true;
    if (filter === "outstanding") return !done;
    if (filter === "required") return e.source === "assigned" && !done;
    if (filter === "optional") return e.source === "elective";
    if (filter === "completed") return done;
    if (filter === "locked") return e.status === "locked";
    return e.complianceStatus === filter || e.status === filter;
  }).sort((a, b) => (a.dueDate || "9") > (b.dueDate || "9") ? 1 : -1), [rows, filter]);

  if (!rows) return <SkeletonPage kpis={0} rows={6} cols={6} testId="my-learning-skeleton" />;
  const outstanding = rows.filter((e) => !DONE.includes(e.status)).length;
  const verb = (e) => DONE.includes(e.status) ? "Review" : e.status === "not_started" ? "Start" : "Continue";

  return (
    <div data-testid="my-learning-page">
      <PageHeader title="My Learning" subtitle={outstanding ? `${outstanding} course${outstanding > 1 ? "s" : ""} still to do, soonest deadline first.` : "You have nothing outstanding — well done."} actions={<HelpLink section="learner-training" />} />
      <div className="flex gap-1 flex-wrap mb-3" role="tablist" aria-label="Filter my learning">
        {FILTERS.map(([k, l]) => <button key={k} role="tab" aria-selected={filter === k} onClick={() => setParams(k === "outstanding" ? {} : { filter: k })} className={cn("h-8 px-3 text-xs font-medium rounded-md border transition-colors", filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:bg-slate-50")} data-testid={`filter-${k}`}>{l}</button>)}
      </div>
      <Panel>
        <DataTable testId="my-learning-table" rows={filtered}
          emptyState={<EmptyState icon={CheckCircle2} title={filter === "outstanding" ? "Nothing outstanding" : "Nothing matches this filter"} hint={filter === "outstanding" ? "Every course assigned to you is complete. Browse Discover if you would like to learn something optional." : "Try another filter, or choose Everything to see all your courses."} />}
          mobileCard={(e) => (
            <div className="space-y-1.5">
              <Link to={`/learn/${e.id}`} className="block text-sm font-medium text-slate-900" data-testid={`learning-link-${e.id}`}>{e.courseTitle}</Link>
              <div className="flex items-center gap-1.5 flex-wrap">
                <StatusBadge status={e.source === "elective" ? e.status : e.complianceStatus} />
                <span className="text-xs text-slate-500">{e.dueDate ? `Due ${fmtDate(e.dueDate)}` : "No deadline"}</span>
              </div>
              <div className="flex items-center gap-2">
                <ProgressBar value={e.progressPercent} tone={DONE.includes(e.status) ? "green" : "sky"} className="flex-1" />
                <span className="text-xs tabular-nums text-slate-600" data-testid={`course-progress-percent-${e.id}`}>{pct(e.progressPercent)}</span>
                <Link to={`/learn/${e.id}`} className="text-xs link font-medium">{verb(e)}</Link>
              </div>
            </div>)}
          columns={[
            { key: "courseTitle", label: "Course", render: (e) => (
              <div className="min-w-0">
                <Link to={`/learn/${e.id}`} className="font-medium text-slate-900 hover:underline" data-testid={`learning-link-${e.id}`}>{e.courseTitle}</Link>
                <span className="ml-2 text-[11px] text-slate-500">{learnerLabel(e.source === "elective" ? "optional" : "required")}</span>
              </div>) },
            { key: "complianceStatus", label: "Status", render: (e) => <StatusBadge status={e.source === "elective" ? e.status : e.complianceStatus} /> },
            { key: "dueDate", label: "Due", render: (e) => e.dueDate ? <span className={cn(e.complianceStatus === "overdue" && "text-red-700 font-medium")}>{fmtDate(e.dueDate)}</span> : <span className="text-slate-400">—</span> },
            { key: "progressPercent", label: "Progress", width: 140, render: (e) => <div className="flex items-center gap-2"><ProgressBar value={e.progressPercent} tone={DONE.includes(e.status) ? "green" : "sky"} className="w-20" /><span className="text-xs tabular-nums" data-testid={`course-progress-percent-${e.id}`}>{pct(e.progressPercent)}</span></div> },
            { key: "lastActivityAt", label: "Last activity", render: (e) => fmtDate(e.lastActivityAt) },
            { key: "act", label: "", render: (e) => <Link to={`/learn/${e.id}`} className="text-xs link font-medium">{verb(e)}</Link> },
          ]} />
      </Panel>
    </div>
  );
}
